// What each plan allows, from one source: the workspace payload's `plans` (policy.LIMITS with each plan's label and
// monthly price) and the registry below of every feature a plan can withhold. Lock chips, cap text, the upgrade
// dialog's lines, the Overview's "what the next plan adds" and the plan comparison all read from here: no screen
// names a plan or a number by hand.

// Every key policy.LIMITS restricts on a lower plan: the screen where it is used, its name and one line on what it
// does. tests/test_praiseboard_copy.py fails when a restricted key has no entry, so a paid feature is never hidden.
export const LOCKED_FEATURES = {
  published: { screen: '/inbox', label: 'More published testimonials', group: 'published', about: 'Collecting and reviewing feedback is unlimited on every plan; the plan sets how many testimonials can be public.' },
  forms: { screen: '/forms', label: 'More collection pages', group: 'forms', about: 'Run more than one collection page, for example one per service or per campaign.' },
  preset_questions: { screen: '/forms', label: 'More questions per page', group: 'forms', about: 'Ask as many questions as you need on each collection page.' },
  custom_questions: { screen: '/forms', label: 'Your own questions', group: 'questions', about: 'Write your own questions and help text, and choose which answer you quote.' },
  template_library: { screen: '/forms', label: 'The full template library', group: 'questions', about: 'Question templates for software, agency projects and consultants, besides the general and service ones.' },
  widgets: { screen: '/widgets', label: 'More widgets', group: 'widgets', about: 'Run several widgets, for example one per page or per product.' },
  layouts: { screen: '/widgets', label: 'Every widget layout', group: 'widgets', about: 'Show testimonials as cards, a carousel or a single quote, as well as the grid.' },
  styling: { screen: '/widgets', label: 'Advanced styling', group: 'widgets', about: 'Choose the font, card style, spacing, columns, order, heading and which details show.' },
  remove_branding: { screen: '/widgets', label: 'No TrustClap credit', group: 'branding', about: 'Hide the small "Collected with TrustClap" credit on your widgets, hosted wall and collection pages.' },
  graphics: { screen: '/graphics', label: 'Every quote graphic', group: 'graphics', about: 'Download quote graphics in every format and template, without the "Made with TrustClap" mark.' },
  reports: { screen: '/reports', label: 'Date-range reports', group: 'reports', about: 'See the last 7 or 90 days or any range, and compare it with the period before.' },
  seats: { screen: '/team', label: 'Team seats', group: 'seats', about: 'Invite teammates with their own login, as editors or admins.' },
  brand_scoped_seats: { screen: '/team', label: 'Brand-level access', group: 'seats', about: 'Let a teammate see only the client brands you choose.' },
  brands: { screen: '/brands', label: 'Client brands', group: 'brands', about: 'Run each client as its own brand, with its own collection pages, inbox, widgets, hosted wall and client approvals.' },
  client_approvals: { screen: '/team', label: 'Client approvals', group: 'approvals', about: 'Send approved testimonials to your client before they are published.' },
  client_reviewers: { screen: '/team', label: 'Client reviewers', group: 'approvals', about: 'Clients approve through a private link that shows only their brand, no login needed.' },
  white_label_approvals: { screen: '/team', label: 'Agency name on client pages', group: 'approvals', about: 'Client approval and report pages show your agency name and logo, not ours.' },
  agency_templates: { screen: '/team', label: 'Reusable templates', group: 'templates', about: 'Save a collection page or a widget as a template and reuse it for every client.' },
  client_reports: { screen: '/team', label: 'Client report links', group: 'client_reports', about: 'Share a read-only page with a brand\'s figures and recent testimonials with your client.' }
};

const count = (value, one, many) => (value === null ? 'Unlimited ' + many : 'Up to ' + value + ' ' + (value === 1 ? one : many));

// One line per group, in this order, from the plan's own limits: what the upgrade dialog, the Overview and the plan
// comparison list as a plan's additions.
const GROUPS = [
  ['published', l => count(l.published, 'published testimonial', 'published testimonials')],
  ['forms', l => count(l.forms, 'collection page', 'collection pages')],
  ['questions', l => [l.custom_questions ? 'Your own questions' : '', l.template_library === 'full' ? 'the full template library' : '']
    .filter(Boolean).join(' and ')],
  ['widgets', l => count(l.widgets, 'widget', 'widgets') + ' in all ' + (l.layouts || []).length + ' layouts'
    + (l.styling === 'advanced' ? ', with advanced styling' : '')],
  ['branding', () => 'No TrustClap credit on your widgets, wall and collection pages'],
  ['graphics', () => 'Quote graphics in every format and template, without the mark'],
  ['reports', () => 'Reports for any period, with comparisons'],
  ['brands', l => 'Up to ' + l.brands + ' active client brands, each with its own forms, widgets and wall'],
  ['approvals', () => 'Client approval links with your agency name, no client login'],
  ['templates', () => 'Reusable form and widget templates'],
  ['client_reports', () => 'Client report links'],
  ['seats', l => l.seats + ' team seats' + (l.brand_scoped_seats ? ' with access to the brands you choose' : '')]
];

// What a capped control counts, for "1 of 1 on Free".
export const COUNTED_UNITS = {
  published: ['published testimonial', 'published testimonials'], forms: ['collection page', 'collection pages'],
  widgets: ['widget', 'widgets'], brands: ['active brand', 'active brands'], seats: ['team seat', 'team seats']
};

function same(a, b) { return JSON.stringify(a) === JSON.stringify(b); }

// The same comparison as policy.allows_more: no cap over a cap, a higher cap, a feature that is on, more widget
// layouts, or a tier beyond the basic one.
export function allowsMore(value, than) {
  if (value === undefined || same(value, than)) return false;
  if (typeof value === 'boolean' || typeof than === 'boolean') return !!value && !than;
  if (Array.isArray(value)) {
    const base = than || [];
    return value.length > base.length && base.every(item => value.indexOf(item) >= 0);
  }
  if (value === null) return than !== null && than !== undefined;
  if (typeof value === 'number') return typeof than === 'number' && value > than;
  return than === 'basic';
}

// The plans cheapest first.
export function planOrder(matrix) {
  return Object.keys(matrix || {}).sort((a, b) => Number(matrix[a].price_monthly) - Number(matrix[b].price_monthly));
}

// The cheapest plan whose limit for `key` allows more than `plan`'s (policy.unlocked_by); null when none does.
export function availableOn(matrix, key, plan) {
  const base = matrix && matrix[plan || 'free'];
  if (!base || !base.limits || !(key in base.limits)) return null;
  return planOrder(matrix).find(name => allowsMore(matrix[name].limits[key], base.limits[key])) || null;
}

export function planLabel(matrix, name) {
  return (matrix && matrix[name] && matrix[name].label) || (name ? name.charAt(0).toUpperCase() + name.slice(1) : '');
}

export function monthlyPrice(matrix, name) {
  const value = Number(matrix && matrix[name] ? matrix[name].price_monthly : 0);
  return '$' + (value % 1 ? value.toFixed(2) : String(value));
}

// "1 of 1 on Free": what a capped control shows.
export function capText(used, allowed, label) {
  return used + ' of ' + allowed + ' on ' + label;
}

// The groups whose features `target` allows more of than `fromPlan`.
function addedGroups(matrix, fromPlan, target) {
  const base = matrix[fromPlan].limits;
  const limits = matrix[target].limits;
  return GROUPS.filter(([group]) => Object.keys(LOCKED_FEATURES)
    .some(key => LOCKED_FEATURES[key].group === group && allowsMore(limits[key], base[key])));
}

// What `target` adds over `fromPlan`, one line per group.
export function addedLines(matrix, fromPlan, target) {
  if (!matrix || !matrix[fromPlan] || !matrix[target]) return [];
  return addedGroups(matrix, fromPlan, target).map(([, line]) => line(matrix[target].limits)).filter(Boolean);
}

// What the upgrade dialog lists for `target`: the clicked feature's line first, then the rest of what the plan adds.
// Two plans up, the plan in between becomes one "Everything in Solo" line.
export function upgradeLines(matrix, fromPlan, target, feature) {
  if (!matrix || !matrix[fromPlan] || !matrix[target]) return [];
  const order = planOrder(matrix);
  const below = order[order.indexOf(target) - 1];
  const skipped = !!below && order.indexOf(below) > order.indexOf(fromPlan);
  const groups = addedGroups(matrix, skipped ? below : fromPlan, target);
  const first = feature && LOCKED_FEATURES[feature] ? LOCKED_FEATURES[feature].group : '';
  const lead = GROUPS.filter(([group]) => group === first);
  const rest = groups.filter(([group]) => group !== first);
  const limits = matrix[target].limits;
  const lines = lead.map(([, line]) => line(limits));
  if (skipped) lines.push('Everything in ' + planLabel(matrix, below));
  return lines.concat(rest.map(([, line]) => line(limits))).filter(Boolean);
}
